import * as cheerio from 'cheerio';
import { AccountFont } from '../types';


export const validateUrl = (value: string): boolean => {
  try {
    new URL(value);
    return true;
  } catch (_) {
    return false;
  }
};

export const validateCss = (value: string): boolean => {
  try {
    const style = document.createElement("style");
    style.textContent = value;
    document.head.appendChild(style);
    document.head.removeChild(style);
    return true;
  } catch (error) {
    return false;
  }
};

export const capitalize = (str: string): string => {
  if (!str) return '';
  return `${str[0].toUpperCase()}${str.slice(1)}`;
};

export const condenseString = (str: string, maxChars = 20): string => {
  if (str.length <= maxChars) return str;
  const firstPart = str.substring(0, maxChars / 2);
  const lastPart = str.substring(str.length - maxChars / 2, str.length);
  return `${firstPart}...${lastPart}`;
};

export const insert = (str: string, substr: string, index: number): string => (
  str.substring(0, index) + substr + str.substring(index)
);

export const replace = (
  str: string, substr: string, startIndex: number, endIndex: number,
): string => (
  str.substring(0, startIndex) + substr + str.substring(endIndex)
);

export const isValidEmail = (email?: string | null | undefined): boolean => {
  if (!email) return false;

  return emailRegex.test(email);
};

export const emailRegex = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);

interface ParseTextResponseForQuestionVariablesProps {
  text: string;
  questionResponseValues: Record<string, string>;
}

export const parseTextResponseForQuestionVariables = ({
  text,
  questionResponseValues,
}: ParseTextResponseForQuestionVariablesProps): string => (
  text?.replace(/{Q\d{1,3}}/g, (match: string) => {
    const key = match.slice(1, -1);
    return questionResponseValues[key] || match;
  }));

export const ripOutHtml = (str?: string): string => {
  if (!str) return '';
  return str
    .replace(/<p[^>]*>|<br\s*\/?>/gi, '\n') // Replace <p> and <br> with newlines
    .replace(/<[^>]*>|&[\w\d]+;/g, '') // Remove remaining HTML tags and entities
    .replace(/\u200B|\u200C|\u200D|\uFEFF/g, '') // Remove zero-width characters
    .replace(/\n\s*\n/g, '\n') // Remove extra blank lines
    .trim();
};

export const ripOutHtmlKeepMeaning = (str?: string): string => {
  if (!str) return '';
  return cheerio.load(str).root().text();
};

export const stripHtmlDocumentWrapper = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<\/?html[^>]*>/gi, '')
    .replace(/<\/?head[^>]*>/gi, '')
    .replace(/<\/?body[^>]*>/gi, '')
    .trim();
};



export const toSnakeCase = (str: string): string => str?.replace(/ /g, '_')?.toLocaleLowerCase() || '';

export const prepareStringForFilename = (str: string): string => {
  if (!str) return '';
  return str
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/[\s-]+/g, '_');
};

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  const cleaned = phoneNumber.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  if (match) return `${match[1]}-${match[2]}-${match[3]}`;
  return phoneNumber;
};

export const formatPhoneNumberPretty = (phoneNumber: string): string => {
  if (!phoneNumber) return '';
  const cleaned = phoneNumber.replace(/\D/g, '');

  const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/);
  if (match) return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
  return phoneNumber;
};

export interface FormatNumberProps {
  number?: number;
  format?: string;
  numDecimalPoints?: number;
}

export const formatNumber = ({
  number,
  format,
  numDecimalPoints = 1,
}: FormatNumberProps): string | number => {
  if (!number) return '';

  switch (format) {
    case 'currency': {
      const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
      });
      return formatter.format(Math.round(number));
    }
    case 'float':
      return parseFloat(`${number}`).toFixed(numDecimalPoints);
    case 'integer':
    default:
      return Math.round(number);
  }
};

export const sanitize = (str: string | null | undefined) => {
  if (str == null) return '';

  const map: { [key: string]: string } = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  const reg = /[&<>"'/]/ig;
  return str.replace(reg, (match) => (map[match]));
};

export const convertToValidHtmlId = (str: string) => str.replace(/[^a-zA-Z0-9\-_:.\s]/g, '');

export const formatAccountFontsForWysiwyg = (accountFonts: AccountFont[]): string[] => (
  accountFonts?.map(font => `${font.name}=${font.name.toLowerCase()}`) || []
);

const performDepthFirstSearchToFindLastTextNode = ($: ReturnType<typeof cheerio.load>): any => {
  let lastTextNode = null;

  const searchTextNode = (element: any) => {
    element.contents().each(function (this: any) {
      if (this.type === 'text' && this.data && this.data.trim().length > 0) {
        lastTextNode = this;
      } else if (this.type === 'tag' && this.children) {
        searchTextNode($(this));
      }
    });
  };

  searchTextNode($('body'));
  return lastTextNode;
}


export const appendRequiredIndicator = (html: string, indicatorText: string = '*'): string => {
  const $ = cheerio.load(html);
  const lastTextNode = performDepthFirstSearchToFindLastTextNode($);

  if (lastTextNode) {
    $(`<span class="text-gray-500 font-normal italic" aria-hidden="true"> ${indicatorText}</span><span class="sr-only">, required</span>`).insertAfter(lastTextNode);
    return stripHtmlDocumentWrapper($('body').html() || html);
  }

  return html;
};

export const prepareStringForId = (text?: string): string => {
  if (!text) return '';
  return text.trim().toLowerCase().replace(/[\s_]+/g, '-').replace(/[^a-z0-9-]/g, '');
};

export const removeCodeBlockStart = ({ language, content }: { language: string; content: string; }) => {
  if (!content) return '';

  const codeBlockStart = `\`\`\`${language}\n`;

  if (content.includes(codeBlockStart)) {
    const contentAfterLanguageBlock = content.split(codeBlockStart).pop() || "";
    return `${contentAfterLanguageBlock.trim()}\n`;
  }

  return content;
};

export const removeCodeBlockEnd = ({ content }: { content: string; }) => {
  if (!content) return '';

  const codeBlockEnd = `\`\`\``;

  if (content.includes(codeBlockEnd)) {
    const contentBeforeClosingTicks = content.split(codeBlockEnd).shift() || "";
    return `${contentBeforeClosingTicks.trim()}\n`;
  }

  return content;
};

/**
 * Strips HTML tags from a string and returns the plain text length
 * @param html - The HTML string to process
 * @returns The length of the plain text content
 */
export const getPlainTextLength = (html: string): number => {
  if (!html) return 0;
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  return doc.body.textContent?.length || 0;
};
